/** Tenants → [Tenant]: Overview · Users · Status · Team Management · Providers · Billing · Security · Audit */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, StatusPill, btnGhost, btnPrimary, btnSmall, errorText, inputCls } from '../../components/finance/financeUi';

const TABS = ['overview', 'users', 'status', 'team', 'providers', 'billing', 'security', 'audit'];
const TAB_LABEL = { overview: 'Overview', users: 'Users', status: 'Status', team: 'Team Management', providers: 'Providers / Report Sources',
                    billing: 'Billing / Plan', security: 'Security', audit: 'Audit' };
const ECON_ROLES = ['iso', 'iso_agent', 'iso_subagent'];

const KV = ({ rows }) => (
  <dl className="grid gap-4 px-5 py-4 sm:grid-cols-3">
    {rows.map(([k, v]) => (
      <div key={k}><dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</dt>
        <dd className="mt-0.5 text-sm font-medium">{v ?? '—'}</dd></div>
    ))}
  </dl>
);

function StatusTab({ tenant, onChanged }) {
  const [check, setCheck] = useState(null);
  const [reason, setReason] = useState('');
  const [deactivate, setDeactivate] = useState(false);
  const [confirm, setConfirm] = useState('');
  const loadCheck = useCallback(() => {
    api.request(`/super-admin/tenants/${tenant.id}/closure-check`).then(setCheck).catch(() => setCheck(null));
  }, [tenant.id]);
  useEffect(() => { loadCheck(); }, [loadCheck]);
  const status = async (action) => {
    setConfirm('');
    try {
      if (action === 'close') {
        await api.request(`/super-admin/tenants/${tenant.id}/close`, { method: 'POST', body: JSON.stringify({ reason, deactivate_users: deactivate }) });
      } else {
        await api.request(`/super-admin/tenants/${tenant.id}/${action}`, { method: 'POST' });
      }
      toast.success(`Tenant ${action === 'close' ? 'closed' : `${action}d`}`);
      onChanged();
      loadCheck();
    } catch (err) { toast.error(errorText(err)); }
  };
  const blocking = (check?.checks || []).filter((c) => c.blocking && !(deactivate && c.key === 'active_users'));
  return (
    <div className="space-y-4 px-5 py-4" data-testid="tenant-status-tab">
      <div className="flex flex-wrap gap-2">
        {['suspended', 'pending_approval'].includes(tenant.status) && (
          <button type="button" className={btnPrimary} onClick={() => status('activate')} data-testid="tenant-activate-btn">Activate</button>
        )}
        {tenant.status === 'active' && (
          <button type="button" className={btnGhost} onClick={() => setConfirm('suspend')} data-testid="tenant-suspend-btn">Suspend</button>
        )}
      </div>
      {confirm && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-rose-500/5 p-3 text-sm" data-testid="tenant-confirm-panel">
          {confirm === 'close' ? 'Close this tenant? Closure is final — it cannot be reactivated.' : 'Suspend this tenant? Its users will be unable to sign in.'}
          <button type="button" className={btnPrimary} onClick={() => status(confirm)} data-testid="tenant-confirm-btn">Yes, {confirm}</button>
          <button type="button" className={btnGhost} onClick={() => setConfirm('')} data-testid="tenant-confirm-cancel">Cancel</button>
        </div>
      )}
      {tenant.status === 'archived' && (
        <p className="rounded-lg border p-4 text-sm" data-testid="tenant-closed-details">
          Closed {tenant.archived_at ? new Date(tenant.archived_at).toLocaleString() : ''}{tenant.closure_reason ? ` · ${tenant.closure_reason}` : ''}. History is retained; closure is final.
        </p>
      )}
      {tenant.status !== 'archived' && check && (
        <div className="rounded-xl border p-4" data-testid="tenant-close-panel">
          <p className="text-sm font-medium">Close / Archive — never a delete; history stays intact.</p>
          <ul className="mt-3 grid gap-1.5 text-sm sm:grid-cols-2">
            {check.checks.map((c) => (
              <li key={c.key} className={c.blocking ? 'text-rose-600' : 'text-muted-foreground'} data-testid={`closure-${c.key}`}>
                {c.label}: {c.count}
              </li>
            ))}
          </ul>
          {blocking.length > 0 && (
            <p className="mt-3 flex items-start gap-2 rounded-lg bg-rose-500/5 p-3 text-sm text-rose-700" data-testid="closure-blocked-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {blocking.some((c) => c.key !== 'active_users')
                ? 'This tenant has an active/historical portfolio. Closing it requires portfolio reassignment or book transfer before final closure.'
                : 'This tenant still has active users. Tick "Deactivate this tenant\'s users on close" to close it.'}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={deactivate} onChange={(e) => setDeactivate(e.target.checked)} data-testid="closure-deactivate-users" />
              Deactivate this tenant's users on close
            </label>
            <input className={`${inputCls} sm:w-72`} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="closure-reason" />
            <button type="button" className={btnPrimary} disabled={blocking.length > 0} onClick={() => setConfirm('close')} data-testid="tenant-close-btn">Close tenant</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTab({ tenantId, economicsOnly }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.request(`/super-admin/users?tenant_id=${tenantId}`).then(setUsers).catch(() => setUsers([])); }, [tenantId]);
  const rows = economicsOnly ? users.filter((u) => ECON_ROLES.includes(u.role)) : users;
  return (
    <ul className="divide-y" data-testid={economicsOnly ? 'tenant-team-tab' : 'tenant-users-tab'}>
      {rows.map((u) => (
        <li key={u.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
          <div className="min-w-0"><p className="truncate font-medium">{u.first_name || u.full_name} {u.last_name || ''}</p>
            <p className="truncate text-xs text-muted-foreground">{u.email} · {String(u.role).replace(/_/g, ' ')}</p></div>
          <div className="flex items-center gap-2">
            <StatusPill value={String(u.status || '').toUpperCase()} />
            {ECON_ROLES.includes(u.role) && (
              <Link to={`/team/${u.id}`} className={btnSmall} data-testid={`team-economics-link-${u.id}`}>Economics</Link>
            )}
          </div>
        </li>
      ))}
      {!rows.length && <li className="px-5 py-10 text-center text-muted-foreground">No users.</li>}
    </ul>
  );
}

function AuditTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.request(`/super-admin/tenants/${tenantId}/audit`).then(setRows).catch(() => setRows([])); }, [tenantId]);
  return (
    <ol className="space-y-1 px-5 py-4 text-xs text-muted-foreground" data-testid="tenant-audit-tab">
      {rows.map((r, i) => <li key={i}>{r.created_at ? new Date(r.created_at).toLocaleString() : ''} · {r.action}</li>)}
      {!rows.length && <li>No audit entries.</li>}
    </ol>
  );
}

export default function TenantProfilePage() {
  const { tenantId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'overview';
  const [tenant, setTenant] = useState(null);
  const [providers, setProviders] = useState([]);
  const load = useCallback(() => {
    api.request(`/super-admin/tenants/${tenantId}`).then(setTenant).catch((e) => toast.error(errorText(e)));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === 'providers') api.request(`/financials/disbursement-accounts?tenant_id=${tenantId}`).then(setProviders).catch(() => setProviders([]));
  }, [tab, tenantId]);
  if (!tenant) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  const contact = tenant.primary_contact || {};
  return (
    <div className="space-y-6" data-testid="tenant-profile-page">
      <Link to="/admin/tenants" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"><ArrowLeft className="h-4 w-4" />Tenants</Link>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Tenant</p>
          <h1 className="mt-1 text-4xl font-semibold tracking-tight" data-testid="tenant-profile-name">{tenant.company_name}</h1>
        </div>
        <StatusPill value={String(tenant.status).toUpperCase()} testid="tenant-profile-status" />
      </header>
      <div className="flex flex-wrap gap-2" data-testid="tenant-profile-tabs">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => setParams({ tab: t })} data-testid={`tenant-tab-${t}`}
                  className={`h-9 rounded-full px-4 text-sm transition-colors ${tab === t ? 'bg-primary text-primary-foreground' : 'border hover:bg-muted'}`}>{TAB_LABEL[t]}</button>
        ))}
      </div>
      <Card title={TAB_LABEL[tab]}>
        {tab === 'overview' && <KV rows={[['Status', tenant.status], ['Requested type', tenant.requested_account_type], ['Business type', tenant.business_type],
          ['Primary contact', `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || tenant.email], ['Email', contact.email || tenant.email],
          ['Phone', contact.phone || tenant.phone], ['Team size', tenant.team_size], ['Current processor', tenant.current_processor],
          ['Active users', tenant.active_users], ['Signup', tenant.signup_source || 'created by MSC'],
          ['Created', tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : null]]} />}
        {tab === 'users' && <UsersTab tenantId={tenantId} />}
        {tab === 'status' && <StatusTab tenant={tenant} onChanged={load} />}
        {tab === 'team' && <UsersTab tenantId={tenantId} economicsOnly />}
        {tab === 'providers' && (
          <ul className="divide-y text-sm" data-testid="tenant-providers-tab">
            {providers.map((p) => <li key={p.provider_account_id} className="px-5 py-3">{p.display_name || p.provider_slug} · {p.status}</li>)}
            <li className="px-5 py-3"><Link to="/processor-connections" className="underline">Manage provider connections &amp; report sources</Link></li>
          </ul>
        )}
        {tab === 'billing' && <KV rows={[['Plan', tenant.plan || tenant.subscription_plan], ['Subscription', tenant.subscription_status], ['Billing mode', tenant.billing_mode]]} />}
        {tab === 'security' && <KV rows={[['MFA required', tenant.mfa_required ? 'Yes' : 'No'], ['Allow downline branding', tenant.allow_downline_branding ? 'Yes' : 'No'], ['Subdomain', tenant.subdomain]]} />}
        {tab === 'audit' && <AuditTab tenantId={tenantId} />}
      </Card>
    </div>
  );
}
